/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
} from "@mui/material";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import FTextField from "components/Form/FTextField";
import ConfigDropdown from "components/Dropdown/ConfigDropdown";
import MDTypography from "components/MDTypography";
import pxToRem from "assets/theme/functions/pxToRem";
import moment from "moment";
import Constants, { defaultData } from "utils/Constants";

const LOAN_TYPE_MENU = [
  { [Constants.MONGOOSE_ID]: "home", title: "Home Loan" },
  { [Constants.MONGOOSE_ID]: "personal", title: "Personal Loan" },
  { [Constants.MONGOOSE_ID]: "business", title: "Business Loan" },
  { [Constants.MONGOOSE_ID]: "car", title: "Car Loan" },
  { [Constants.MONGOOSE_ID]: "education", title: "Education Loan" },
  { [Constants.MONGOOSE_ID]: "credit", title: "Credit Card" },
  { [Constants.MONGOOSE_ID]: "loanAgainstProperty", title: "Loan Against Property" },
  { [Constants.MONGOOSE_ID]: "loanAgainstSecurity", title: "Loan Against Security" },
  { [Constants.MONGOOSE_ID]: "workingCapital", title: "Working Capital" },
  { [Constants.MONGOOSE_ID]: "machinery", title: "Machinery Loan" },
  { [Constants.MONGOOSE_ID]: "gold", title: "Gold Loan" },
];

const COUNTRY_MENU = [
  "India",
  "United States of America (USA)",
  "Canada",
  "United Kingdom (UK)",
  "Australia",
  "New Zealand",
  "Germany",
  "France",
  "Ireland",
  "Netherlands",
  "Sweden",
  "Italy",
  "Spain",
  "Switzerland",
  "Singapore",
  "Malaysia",
  "United Arab Emirates (UAE)",
  "Poland",
  "Czech Republic",
  "Hungary",
  "Latvia",
  "Lithuania",
  "Other",
].map((country) => ({
  [Constants.MONGOOSE_ID]: country,
  title: country,
}));

const formatNumberWithCommas = (val) => {
  if (val === undefined || val === null) return "";
  const clean = val.toString().replace(/,/g, "");
  if (clean === "") return "";
  const num = Number(clean);
  if (Number.isNaN(num)) return clean;
  return num.toLocaleString("en-IN");
};

const initialForm = {
  firstName: "",
  lastName: "",
  mobileNumber: "",
  imd: "",
  loanType: "home",
  loanAmount: "",
  leadDate: moment().format(defaultData.DATABSE_DATE_FORMAT),
  salesManager: "",
  email: "",
  modelOfCar: "",
  carLoanType: "newCarLoan",
  universityLetterReceived: "yes",
  countryApplyingFor: "",
};

export default function EditInquiryModal({
  open,
  handleClose,
  handleSubmit,
  loading,
  inquiry,
  salesManagerOptions = [],
}) {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && inquiry) {
      setFormData({
        firstName: inquiry.firstName || "",
        lastName: inquiry.lastName || "",
        mobileNumber: inquiry.mobileNumber || "",
        imd: inquiry.imd || "",
        loanType: inquiry.loanType || "home",
        loanAmount:
          inquiry.loanAmount !== undefined && inquiry.loanAmount !== null
            ? String(inquiry.loanAmount).replace(/,/g, "")
            : "",
        leadDate: inquiry.leadDate
          ? moment(inquiry.leadDate).format(defaultData.DATABSE_DATE_FORMAT)
          : moment().format(defaultData.DATABSE_DATE_FORMAT),
        salesManager:
          inquiry.salesManager?.[Constants.MONGOOSE_ID] ||
          inquiry.salesManager?.id ||
          (typeof inquiry.salesManager === "string" ? inquiry.salesManager : ""),
        email: inquiry.email || "",
        modelOfCar: inquiry.modelOfCar || inquiry.carModel || "",
        carLoanType: inquiry.carLoanType || inquiry.carType || "newCarLoan",
        universityLetterReceived: inquiry.universityLetterReceived || "yes",
        countryApplyingFor: inquiry.countryApplyingFor || inquiry.countryOfApplication || "",
      });
      setErrors({});
    }
  }, [open, inquiry]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "mobileNumber") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "loanAmount") {
      finalValue = value.toString().replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => {
      if (name === "loanType") {
        return {
          ...prev,
          loanType: value,
          email: "",
          modelOfCar: "",
          carLoanType: value === "car" ? "newCarLoan" : "",
          universityLetterReceived: value === "education" ? "yes" : "",
          countryApplyingFor: "",
        };
      }
      return { ...prev, [name]: value };
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "loanType") {
      setErrors((prev) => ({
        ...prev,
        email: "",
        modelOfCar: "",
        carLoanType: "",
        universityLetterReceived: "",
        countryApplyingFor: "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName || formData.firstName.trim() === "") {
      newErrors.firstName = "First name is required";
    }

    if (!formData.mobileNumber || formData.mobileNumber.trim() === "") {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    }

    const cleanAmount = formData.loanAmount ? String(formData.loanAmount).replace(/,/g, "") : "";
    if (cleanAmount && Number(cleanAmount) < 0) {
      newErrors.loanAmount = "Loan amount must be positive";
    }

    if (formData.loanType === "home" && formData.email && formData.email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Enter a valid email address";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = () => {
    if (validate()) {
      const cleanAmount = formData.loanAmount
        ? Number(String(formData.loanAmount).replace(/,/g, ""))
        : 0;
      handleSubmit({
        ...formData,
        loanAmount: cleanAmount,
        email: formData.loanType === "home" ? formData.email.trim() : "",
        modelOfCar: formData.loanType === "car" ? formData.modelOfCar.trim() : "",
        carLoanType: formData.loanType === "car" ? formData.carLoanType || "newCarLoan" : null,
        universityLetterReceived:
          formData.loanType === "education" ? formData.universityLetterReceived || "yes" : null,
        countryApplyingFor:
          formData.loanType === "education" ? formData.countryApplyingFor.trim() : "",
      });
    }
  };

  const modalTitle = inquiry?.inquiryId
    ? `Edit Inquiry (INQ-${inquiry.inquiryId})`
    : "Edit Inquiry";

  return (
    <BasicModal
      open={open}
      handleClose={handleClose}
      title={modalTitle}
      actionButton={loading ? "Saving..." : "Save Changes"}
      handleAction={onSubmit}
      minWidth={600}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FTextField
            label="First Name*"
            placeholder="Enter First Name"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            handleChange={handleChange}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FTextField
            label="Last Name"
            placeholder="Enter Last Name"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            handleChange={handleChange}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FTextField
            label="Mobile Number*"
            placeholder="Enter Mobile Number"
            id="mobileNumber"
            name="mobileNumber"
            value={formData.mobileNumber}
            handleChange={handleChange}
            error={Boolean(errors.mobileNumber)}
            helperText={errors.mobileNumber}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  +91
                </InputAdornment>
              ),
              inputProps: {
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FTextField
            label="IMD"
            placeholder="Enter IMD code"
            id="imd"
            name="imd"
            value={formData.imd}
            handleChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <ConfigDropdown
            label="Loan Type*"
            id="loanType"
            name="loanType"
            value={formData.loanType}
            handleChange={(name, value) => handleDropdownChange(name, value)}
            menu={LOAN_TYPE_MENU}
            error={Boolean(errors.loanType)}
            helperText={errors.loanType}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FTextField
            label="Loan Amount (₹)"
            placeholder="Enter Loan Amount"
            id="loanAmount"
            name="loanAmount"
            type="text"
            value={formatNumberWithCommas(formData.loanAmount)}
            handleChange={handleChange}
            error={Boolean(errors.loanAmount)}
            helperText={errors.loanAmount}
            inputProps={{
              inputMode: "numeric",
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FTextField
            label="Lead Date"
            placeholder="Select Lead Date"
            id="leadDate"
            name="leadDate"
            type="date"
            value={formData.leadDate}
            handleChange={handleChange}
            InputProps={{
              inputProps: { max: new Date().toISOString().split("T")[0] },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ConfigDropdown
            label="Sales Manager"
            id="salesManager"
            name="salesManager"
            value={formData.salesManager}
            handleChange={(name, value) => handleDropdownChange(name, value)}
            menu={salesManagerOptions}
            hint="Select Sales Manager"
          />
        </Grid>

        {/* Loan-Specific Conditional Fields */}
        {formData.loanType === "home" && (
          <Grid item xs={12} sm={6}>
            <FTextField
              label="Email Address"
              placeholder="Enter Email Address"
              id="email"
              name="email"
              type="email"
              value={formData.email}
              handleChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Grid>
        )}

        {formData.loanType === "car" && (
          <>
            <Grid item xs={12} sm={6}>
              <FTextField
                label="Car Model"
                placeholder="Enter Car Model (e.g. Swift, Creta)"
                id="modelOfCar"
                name="modelOfCar"
                value={formData.modelOfCar}
                handleChange={handleChange}
                error={Boolean(errors.modelOfCar)}
                helperText={errors.modelOfCar}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset" error={Boolean(errors.carLoanType)} sx={{ mt: 1 }}>
                <FormLabel
                  component="legend"
                  sx={{
                    fontSize: pxToRem(14),
                    fontWeight: 500,
                    color: "#344054",
                    "&.Mui-focused": { color: "#344054" },
                  }}
                >
                  Car Type
                </FormLabel>
                <RadioGroup
                  row
                  name="carLoanType"
                  value={formData.carLoanType || "newCarLoan"}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="newCarLoan"
                    control={
                      <Radio
                        size="small"
                        sx={{ color: "#0070FF", "&.Mui-checked": { color: "#0070FF" } }}
                      />
                    }
                    label={
                      <MDTypography
                        variant="caption"
                        sx={{ fontSize: pxToRem(14), color: "#344054" }}
                      >
                        New
                      </MDTypography>
                    }
                  />
                  <FormControlLabel
                    value="usedCarLoan"
                    control={
                      <Radio
                        size="small"
                        sx={{ color: "#0070FF", "&.Mui-checked": { color: "#0070FF" } }}
                      />
                    }
                    label={
                      <MDTypography
                        variant="caption"
                        sx={{ fontSize: pxToRem(14), color: "#344054" }}
                      >
                        Used
                      </MDTypography>
                    }
                  />
                </RadioGroup>
                {errors.carLoanType && (
                  <FormHelperText sx={{ color: "red", ml: 0 }}>{errors.carLoanType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </>
        )}

        {formData.loanType === "education" && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl
                component="fieldset"
                error={Boolean(errors.universityLetterReceived)}
                sx={{ mt: 1 }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    fontSize: pxToRem(14),
                    fontWeight: 500,
                    color: "#344054",
                    "&.Mui-focused": { color: "#344054" },
                  }}
                >
                  Have you received the admission letter from the university?
                </FormLabel>
                <RadioGroup
                  row
                  name="universityLetterReceived"
                  value={formData.universityLetterReceived || "yes"}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        size="small"
                        sx={{ color: "#0070FF", "&.Mui-checked": { color: "#0070FF" } }}
                      />
                    }
                    label={
                      <MDTypography
                        variant="caption"
                        sx={{ fontSize: pxToRem(14), color: "#344054" }}
                      >
                        Yes
                      </MDTypography>
                    }
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        size="small"
                        sx={{ color: "#0070FF", "&.Mui-checked": { color: "#0070FF" } }}
                      />
                    }
                    label={
                      <MDTypography
                        variant="caption"
                        sx={{ fontSize: pxToRem(14), color: "#344054" }}
                      >
                        No
                      </MDTypography>
                    }
                  />
                </RadioGroup>
                {errors.universityLetterReceived && (
                  <FormHelperText sx={{ color: "red", ml: 0 }}>
                    {errors.universityLetterReceived}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ConfigDropdown
                label="Country of Application"
                id="countryApplyingFor"
                name="countryApplyingFor"
                value={formData.countryApplyingFor}
                handleChange={(name, value) => handleDropdownChange(name, value)}
                menu={COUNTRY_MENU}
                hint="Select Country"
                error={Boolean(errors.countryApplyingFor)}
                helperText={errors.countryApplyingFor}
              />
            </Grid>
          </>
        )}
      </Grid>
    </BasicModal>
  );
}
